import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import PrescriptionPrintRouter from "./pages/doctor/PrescriptionPrintRouter";

import Navbar       from "./components/Navbar";
import WellnessGuideBot from "./components/WellnessGuideBot";
import RequireAdmin from "./components/RequireAdmin";
import AdminLayout  from "./layouts/AdminLayout";
import { ToastProvider } from "./components/ToastProvider";

/* PUBLIC */
import Home        from "./pages/Home";
import About       from "./pages/About";
import Services    from "./pages/Services";
import RequestPage from "./pages/RequestPage";
import StatusPage  from "./pages/StatusPage";
import OurDoctors  from "./pages/OurDoctors";

/* ADMIN */
import AdminLogin              from "./pages/AdminLogin";
import AdminForgotPassword     from "./pages/AdminForgotPassword";
import AdminResetPassword      from "./pages/AdminResetPassword";
import AdminDashboard          from "./pages/admin/AdminDashboard";
import AdminSite               from "./pages/admin/AdminSite";
import AdminUsers              from "./pages/admin/AdminUsers";
import AdminDoctors            from "./pages/admin/AdminDoctors";
import AdminSettings           from "./pages/admin/AdminSettings";
import AdminOnlineConsultation from "./pages/admin/AdminOnlineConsultation";
import AdminDoctorProfiles     from "./pages/admin/AdminDoctorProfiles";
import AdminDailyPosts         from "./pages/admin/AdminDailyPosts";
import AdminSeasonalRegimen    from "./pages/admin/AdminSeasonalRegimen";
import AdminMRD                from "./pages/admin/AdminMRD";
import AdminOpdViewer          from "./pages/admin/AdminOpdViewer";
import AdminMedicines          from "./pages/admin/AdminMedicines"; // MASTER ADMIN ONLY — hidden panel
import AdminDiseases           from "./pages/admin/AdminDiseases";   // MASTER ADMIN ONLY — hidden panel
import AdminAnalytics          from "./pages/admin/AdminAnalytics";
import AdminWhatsApp           from "./pages/admin/AdminWhatsApp"; // ← NEW
import AdminChatbot            from "./pages/admin/AdminChatbot";

/* DOCTOR */
import DoctorRoutes from "./routes/DoctorRoutes";

function isPublicRoute(pathname) {
  return !(
    pathname.startsWith("/admin") ||
    pathname.startsWith("/clinic-management") ||
    pathname.startsWith("/staff-portal") ||
    pathname.startsWith("/print")
  );
}

function ConditionalNavbar() {
  const loc = useLocation();
  if (!isPublicRoute(loc.pathname)) return null;
  return <Navbar />;
}

function ConditionalWellnessBot() {
  const loc = useLocation();
  if (!isPublicRoute(loc.pathname)) return null;
  return <WellnessGuideBot />;
}

function AdminPage({ children, masterOnly, dashboardAdminOk }) {
  return (
    <RequireAdmin masterOnly={masterOnly} dashboardAdminOk={dashboardAdminOk}>
      <AdminLayout>{children}</AdminLayout>
    </RequireAdmin>
  );
}

function AnimatedRoutes() {
  const loc = useLocation();

  if (loc.pathname.startsWith("/print")) {
    return (
      <Routes>
        <Route path="/print/handwritten" element={<PrescriptionPrintRouter />} />
      </Routes>
    );
  }

  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={loc.pathname}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
      >
        <Routes location={loc}>

          {/* PUBLIC */}
          <Route path="/"            element={<Home />} />
          <Route path="/about"       element={<About />} />
          <Route path="/services"    element={<Services />} />
          <Route path="/request"     element={<RequestPage />} />
          <Route path="/status"      element={<StatusPage />} />
          <Route path="/our-doctors" element={<OurDoctors />} />

          {/* ADMIN LOGIN */}
          <Route path="/clinic-management/signin" element={<AdminLogin />} />
          <Route path="/clinic-management/forgot-password" element={<AdminForgotPassword />} />
          <Route path="/clinic-management/reset-password"  element={<AdminResetPassword />} />

          {/* ADMIN PROTECTED */}
          <Route path="/admin"          element={<AdminPage><AdminDashboard /></AdminPage>} />
          <Route path="/admin/requests" element={<AdminPage><AdminDashboard /></AdminPage>} />
          <Route path="/admin/site"     element={<AdminPage><AdminSite /></AdminPage>} />
          <Route path="/admin/settings" element={<AdminPage><AdminSettings /></AdminPage>} />

          {/* OPD VIEWER — MASTER_ADMIN + DASHBOARD_ADMIN */}
          <Route path="/admin/opd-viewer"
            element={<AdminPage dashboardAdminOk><AdminOpdViewer /></AdminPage>} />

          {/* MASTER ADMIN ONLY */}
          <Route path="/admin/online-consultation" element={<AdminPage masterOnly><AdminOnlineConsultation /></AdminPage>} />
          <Route path="/admin/users"           element={<AdminPage masterOnly><AdminUsers /></AdminPage>} />
          <Route path="/admin/doctors"         element={<AdminPage masterOnly><AdminDoctors /></AdminPage>} />
          <Route path="/admin/medicines"       element={<AdminPage masterOnly><AdminMedicines /></AdminPage>} />
          <Route path="/admin/diseases"        element={<AdminPage masterOnly><AdminDiseases /></AdminPage>} />
          <Route path="/admin/analytics"       element={<AdminPage masterOnly><AdminAnalytics /></AdminPage>} />
          <Route path="/admin/whatsapp"        element={<AdminPage masterOnly><AdminWhatsApp /></AdminPage>} /> {/* ← NEW */}
          <Route path="/admin/chatbot"         element={<AdminPage masterOnly><AdminChatbot /></AdminPage>} />
          <Route path="/admin/doctor-profiles" element={<AdminPage><AdminDoctorProfiles /></AdminPage>} />
          <Route path="/admin/daily-posts"      element={<AdminPage><AdminDailyPosts /></AdminPage>} />
          <Route path="/admin/seasonal-regimen" element={<AdminPage><AdminSeasonalRegimen /></AdminPage>} />
          <Route path="/admin/mrd"             element={<AdminPage><AdminMRD /></AdminPage>} />

          {/* DOCTOR */}
          <Route path="/staff-portal/*" element={<DoctorRoutes />} />

        </Routes>
      </motion.div>
    </AnimatePresence>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ToastProvider>
        <ConditionalNavbar />
        <AnimatedRoutes />
        <ConditionalWellnessBot />
      </ToastProvider>
    </BrowserRouter>
  );
}